import React, { type ReactNode, useState } from 'react';

import { CheckOutlined, CloseOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Card, Form, FormInstance, Row, Space, Typography } from 'antd';
import { useMessageLogger } from 'hooks/useMessageLogger';
import { type Mutation } from 'models/helper';
import { useAppSelector } from 'services/redux/configureStore';

import styles from './Card.module.css';
import { type CardField, Field } from '../ui/Field';

const { Title } = Typography;

export interface EditableCardProps {
  title: string;
  headerPanel?: ReactNode;
  fields: CardField[];
  mutation: Mutation;
  data: Record<string, unknown>;
  normalizeValues?: (values: any) => any;
  withId?: boolean;
  externalForm?: FormInstance<object>;
}

export const EditableCard = ({
  headerPanel,
  title,
  fields,
  mutation,
  data,
  normalizeValues,
  withId,
  externalForm,
}: EditableCardProps) => {
  const isDarkMode = useAppSelector((state) => state.theme.isDarkMode);
  const [internalForm] = Form.useForm();
  const form = externalForm ?? internalForm;
  const [isEditing, setIsEditing] = useState(false);
  const [sendData, { error, status }] = mutation;

  const onFinish = () => {
    const filteredData = Object.fromEntries(
      Object.entries(form.getFieldsValue()).filter((i) => i[1] !== undefined && i[1] !== null)
    );

    if (normalizeValues) {
      sendData(normalizeValues(filteredData));
      return;
    }
    sendData(filteredData);
  };

  const onEdit = () => {
    setIsEditing(true);
    form.setFieldsValue(data);
  };

  const onSuccess = () => setIsEditing(false);

  useMessageLogger({ error, successMessage: 'Информация обновлена', status, onSuccess });

  const titleNode = () => {
    return (
      <Row justify="space-between">
        <Title level={5}>{title}</Title>
        <div>
          {headerPanel && <Title level={5}>{headerPanel}</Title>}
          <div>
            <>
              {isEditing ? (
                <Space>
                  <Button
                    id="me-edit-mode-save-changes"
                    icon={<CheckOutlined />}
                    onClick={() => form.submit()}
                  />
                  <Button
                    id="me-close-edit-mode-button"
                    icon={<CloseOutlined />}
                    danger
                    onClick={() => setIsEditing(false)}
                  />
                </Space>
              ) : (
                <Button id="me-open-edit-mode-button" icon={<EditOutlined />} onClick={() => onEdit()} />
              )}
            </>
          </div>
        </div>
      </Row>
    );
  };

  return (
    <Form form={form} onFinish={onFinish}>
      {withId && <Form.Item name="id" hidden></Form.Item>}
      <Card
        bordered={false}
        className={`${styles.card} ${isDarkMode ? styles.card_dark : ''}`}
        title={titleNode()}
        style={{
          boxShadow: isDarkMode ? 'rgb(255 255 255 / 20%) 0px 0px 2px 0px' : '0px 0px 2px 0 rgb(192 192 192)',
        }}
      >
        {fields.map((field) => (
          <Field key={field.label} isEditing={isEditing} {...field} />
        ))}
      </Card>
    </Form>
  );
};
